<template>
    <div class="container">
        <span v-if="item" class="text-h3">{{ item.title }}</span>
        <v-row align="center" justify="center" style="height: 11vh">
            <v-col cols="12">
                <span v-if="item" @click="openDialog(actualHour)" style="cursor: pointer" class="text-center text-h6">{{
                    getTitleHour(actualHour) }}</span>
            </v-col>
        </v-row>

        <v-row align="center" justify="center">
            <v-col cols="12">
                <canvas ref="clockCanvas" @click="handleCanvasClick" @mousemove="handleMouseMove"></canvas>
            </v-col>
        </v-row>


        <v-dialog :value="dialog" width="80%" @input="setDialog">
            <v-card class="pa-4">
                <v-card-title>{{ dialogTitle }}</v-card-title>
                <v-card-text v-html="dialogText"></v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { APIService } from "@/http/APIService.js";
const apiService = new APIService();
const CLOCK_TIME_SCALE = (12 * 60 * 60 * 1000) / (60 * 1000);
const MAX_DEVICE_PIXEL_RATIO = 2;

export default {
    name: "clocks",
    data() {
        return {
            item: null,
            dialog: false,
            dialogText: "",
            dialogTitle: "",
            hours: Array.from({ length: 12 }, (_, i) => i + 1),
            radius: 90,
            centerX: 100,
            centerY: 100,
            size: 200,
            ctx: null,
            actualHour: 12,
            animationFrameId: null,
            pageHidden: document.hidden
        };
    },
    mounted() {
        this.clockElapsedTimeMs = 0;
        this.clockLastTimestamp = window.performance.now();
        this.clockRunning = true;
        this.getItem();
        this.drawClock();
        this.resizeHandler(null);
    },

    created() {
        window.addEventListener("resize", this.resizeHandler);
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
    },
    beforeDestroy() {
        this.clockRunning = false;
        window.removeEventListener("resize", this.resizeHandler);
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        if (this.animationFrameId !== null) {
            window.cancelAnimationFrame(this.animationFrameId);
        }
        this.animationFrameId = null;
    },


    methods: {
        resizeHandler(e) {
            if (this.$vuetify.breakpoint.smAndDown) {
                this.size = 275;
            } else {
                this.size = 500;
            }

            this.radius = this.size / 2.22;
            this.centerX = this.size / 2;
            this.centerY = this.size / 2;
        },

        getItem() {
            apiService.getClocksPage().then(response => {
                this.item = response;
            });
        },

        getTitleHour(hour) {
            if (!this.item[`clock_${hour}_title`])
                return "";
            return this.item[`clock_${hour}_title`]; // Access properties directly
        },

        openDialog(hour) {
            this.setDialog(true);
            this.dialogText = this.item[`clock_${hour}_text`]; // Access properties directly
            this.dialogTitle = this.item[`clock_${hour}_title`]; // Access properties directly
        },

        setDialog(value) {
            if (value === this.dialog)
                return;

            const timestamp = window.performance.now();
            this.advanceClock(timestamp, this.dialog || this.pageHidden);
            this.dialog = value;
        },

        handleVisibilityChange() {
            const timestamp = window.performance.now();
            this.advanceClock(timestamp, this.dialog || this.pageHidden);
            this.pageHidden = document.hidden;
        },

        advanceClock(timestamp, paused) {
            if (this.clockLastTimestamp !== null && !paused) {
                const elapsedRealTime = Math.max(0, timestamp - this.clockLastTimestamp);
                this.clockElapsedTimeMs += elapsedRealTime * CLOCK_TIME_SCALE;
            }
            this.clockLastTimestamp = timestamp;
        },

        drawClock() {
            const canvas = this.$refs.clockCanvas;
            const ctx = canvas.getContext("2d");
            const faceCanvas = document.createElement("canvas");
            const faceCtx = faceCanvas.getContext("2d");
            let cachedCanvasSize = 0;
            let cachedHour = null;

            if (!ctx || !faceCtx)
                return;

            this.ctx = ctx;

            const updateTime = timestamp => {
                if (!this.clockRunning)
                    return;

                this.advanceClock(timestamp, this.dialog || this.pageHidden);
                const elapsedTime = new Date(this.clockElapsedTimeMs);
                const activeHour = this.getActiveHour(elapsedTime);

                const scale = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
                const canvasSize = Math.floor(this.size * scale);
                const displaySize = `${this.size}px`;

                if (canvas.style.width !== displaySize || canvas.style.height !== displaySize) {
                    canvas.style.width = displaySize;
                    canvas.style.height = displaySize;
                }

                if (canvas.width !== canvasSize || canvas.height !== canvasSize) {
                    canvas.width = canvasSize;
                    canvas.height = canvasSize;
                }

                if (cachedCanvasSize !== canvasSize || cachedHour !== activeHour) {
                    faceCanvas.width = canvasSize;
                    faceCanvas.height = canvasSize;
                    faceCtx.setTransform(1, 0, 0, 1, 0, 0);
                    faceCtx.clearRect(0, 0, canvasSize, canvasSize);
                    const faceRadius = canvasSize * 0.45;
                    faceCtx.translate(canvasSize / 2, canvasSize / 2);
                    this.drawFace(faceCtx, faceRadius);
                    this.drawNumbers(faceCtx, faceRadius, activeHour);
                    cachedCanvasSize = canvasSize;
                    cachedHour = activeHour;
                }

                if (this.actualHour !== activeHour)
                    this.actualHour = activeHour;

                ctx.setTransform(1, 0, 0, 1, 0, 0);
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(faceCanvas, 0, 0);
                const radius = canvas.height * 0.45;
                ctx.translate(canvas.width / 2, canvas.height / 2);

                this.drawTime(ctx, radius, elapsedTime);
                if (this.clockRunning)
                    this.animationFrameId = window.requestAnimationFrame(updateTime);
            };

            this.animationFrameId = window.requestAnimationFrame(updateTime);
        },

        drawFace(ctx, radius) {
            ctx.beginPath();
            ctx.arc(0, 0, radius, 0, 2 * Math.PI);
            ctx.fillStyle = 'white';
            ctx.fill();
            ctx.lineWidth = radius * 0.05;
            ctx.stroke();


            ctx.beginPath();
            ctx.arc(0, 0, radius * 0.06, 0, 2 * Math.PI);
            ctx.fillStyle = '#333';
            ctx.fill();
        },

        getActiveHour(elapsedTime) {
            const hour = elapsedTime.getHours() % 12;
            return hour === 0 ? 12 : hour;
        },

        drawNumbers(ctx, radius, activeHour) {
            ctx.font = radius * 0.15 + "px arial";
            ctx.textBaseline = "middle";
            ctx.textAlign = "center";
            for (let num = 1; num < 13; num++) {
                let ang = num * Math.PI / 6;
                ctx.rotate(ang);
                ctx.translate(0, -radius * 0.85);
                ctx.rotate(-ang);
                if (num === activeHour) {
                    ctx.font = "bold " + radius * 0.25 + "px arial";
                }
                else {
                    ctx.font = radius * 0.15 + "px arial";
                }

                ctx.fillText(num.toString(), 0, 0);
                ctx.rotate(ang);
                ctx.translate(0, radius * 0.85);
                ctx.rotate(-ang);
            }
        },

        drawTime(ctx, radius, elapsedTime) {
            let hour = elapsedTime.getHours();
            let minute = elapsedTime.getMinutes();
            let second = elapsedTime.getSeconds() + elapsedTime.getMilliseconds() / 1000;


            //hour
            hour = hour % 12;
            hour = (hour * Math.PI / 6) +
                (minute * Math.PI / (6 * 60)) +
                (second * Math.PI / (360 * 60));
            this.drawHand(ctx, hour, radius * 0.5, radius * 0.07);
            //minute
            minute = (minute * Math.PI / 30) + (second * Math.PI / (30 * 60));
            this.drawHand(ctx, minute, radius * 0.8, radius * 0.07);
            // second
            // second = (second * Math.PI / 30);
            // this.drawHand(ctx, second, radius * 0.9, radius * 0.02);
        },

        drawHand(ctx, pos, length, width) {
            ctx.beginPath();
            ctx.lineWidth = width;
            ctx.lineCap = "round";
            ctx.moveTo(0, 0);
            ctx.rotate(pos);
            ctx.lineTo(0, -length);
            ctx.stroke();
            ctx.rotate(-pos);
        },

        handleMouseMove(event) {
            const canvas = this.$refs.clockCanvas;
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            canvas.style.cursor = "";
            this.hours.forEach((hour, index) => {
                let angle = (index - 2) * (Math.PI * 2) / 12; // Adjusted angle calculation
                let hx = this.centerX + Math.cos(angle) * (this.radius - 40);
                let hy = this.centerY + Math.sin(angle) * (this.radius - 40);

                const dx = x - hx;
                const dy = y - hy;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < 30) {
                    canvas.style.cursor = "pointer";
                }
            });
        },

        handleCanvasClick(event) {
            const canvas = this.$refs.clockCanvas;
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            this.hours.forEach((hour, index) => {
                let angle = (index - 2) * (Math.PI * 2) / 12; // Adjusted angle calculation
                let hx = this.centerX + Math.cos(angle) * (this.radius - 40);
                let hy = this.centerY + Math.sin(angle) * (this.radius - 40);

                const dx = x - hx;
                const dy = y - hy;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < 30) {
                    this.openDialog(hour);
                }
            });
        }
    }
};
</script>

<style>
.clock {
    position: relative;
    border-radius: 50%;
    box-shadow: 0 15px 25px rgba(0, 0, 0, 0.1), 0 25px 45px rgba(0, 0, 0, 0.1);
    background: #fff;
}
</style>
